import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentsIcon from "@mui/icons-material/Payments";
import CalculateIcon from "@mui/icons-material/Calculate";
import AssessmentIcon from "@mui/icons-material/Assessment";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Layout from "../components/layout/LandingPageLayout";

const Services = () => {
  const theme = useTheme();

  const services = [
    {
      icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
      title: "Accounting and Bookkeeping",
      description:
        "Our accounting and bookkeeping services are tailored to meet the individual needs of your business. We provide comprehensive financial solutions to help you manage your financial records and reporting requirements.",
    },
    {
      icon: <PaymentsIcon sx={{ fontSize: 48 }} />,
      title: "Payroll Processing",
      description:
        "We understand the importance of accurate and timely payroll processing. Our team of experts can help you streamline your payroll process, ensure compliance, and reduce errors.",
    },
    {
      icon: <CalculateIcon sx={{ fontSize: 48 }} />,
      title: "Tax Planning and Preparation",
      description:
        "Our tax planning and preparation services can help you minimize your tax liability and ensure compliance with all relevant tax laws and regulations.",
    },
    {
      icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
      title: "Financial Reporting",
      description:
        "We provide timely and accurate financial reporting services to help you make informed business decisions and stay on top of your financial performance.",
    },
    {
      icon: <VerifiedUserIcon sx={{ fontSize: 48 }} />,
      title: "Audit, Due Diligence and Compliances",
      description:
        "Our Audit, Due Diligence and compliances services can help you optimize your resources and minimize liability against any unwarranted situations, it will ensure compliance with all relevant laws and regulations applicable.",
    },
  ];

  const whyFAI = [
    "Real Time Data",
    "Informed Decision making",
    "Efficiency",
    "Accuracy",
    "Speed",
    "Scalability",
    "Insights",
    "Compliance",
    "Cost-effectiveness",
    "Integration (Existing ERPs)",
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const MotionCard = motion(Card);

  return (
    <Layout>
      <Box
        sx={{
          py: 8,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.05
          )} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="lg">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              align="center"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h6"
              align="center"
              color="text.secondary"
              sx={{ mb: 6, maxWidth: 800, mx: "auto" }}
            >
              Comprehensive financial solutions tailored to your business needs
            </Typography>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={4} sx={{ mb: 8 }}>
              {services.map((service, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <MotionCard
                    variants={cardVariants}
                    whileHover={{
                      y: -8,
                      boxShadow: theme.shadows[12],
                    }}
                    sx={{
                      height: "100%",
                      background: theme.palette.background.paper,
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}`,
                    }}
                  >
                    <CardContent sx={{ p: 4 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          p: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                          mb: 3,
                        }}
                      >
                        {service.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.dark,
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ lineHeight: 1.7 }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </motion.div>

          {/* Why f-ai Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: theme.palette.primary.contrastText,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardContent sx={{ p: 5 }}>
                <Typography
                  variant="h3"
                  component="h2"
                  gutterBottom
                  align="center"
                  sx={{
                    fontWeight: 700,
                    mb: 4,
                  }}
                >
                  Why f-ai?
                </Typography>
                <Divider
                  sx={{
                    mb: 4,
                    bgcolor: alpha(theme.palette.primary.contrastText, 0.3),
                    height: 2,
                  }}
                />
                <Grid container spacing={2}>
                  {whyFAI.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", py: 1 }}
                        >
                          <CheckCircleIcon
                            sx={{
                              mr: 2,
                              fontSize: 28,
                              color: theme.palette.primary.contrastText,
                            }}
                          />
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 500,
                            }}
                          >
                            {index + 1}. {item}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>
    </Layout>
  );
};

export default Services;
